import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import GMBAuthGuard from '@/features/gmb/components/auth/GMBAuthGuard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Progress } from '@/components/ui/progress'
import {
  MapPin,
  Star,
  MessageSquare,
  TrendingUp,
  Building,
  Globe,
  Download,
  Settings,
  RefreshCw
} from 'lucide-react'
import {
  discoverAllAccounts,
  checkOAuthStatus,
  generateOAuthUrl
} from '@/features/gmb/api'
import { supabase } from '@/lib/supabase'
import { useQuery } from '@tanstack/react-query'
import { useGMBStats } from '@/hooks/useGMBStats'
import { Skeleton } from '@/components/ui/skeleton'
import { TrendingDown } from 'lucide-react'

interface LocationMetrics {
  totalReviews: number
  averageRating: number
  newReviewsThisWeek: number
  unansweredQuestions: number
  viewsThisMonth: number
  searchesThisMonth: number
}

export default function GMBLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [oauthStatus, setOauthStatus] = useState<{
    isConfigured: boolean
    accountCount: number
    locationCount: number
    lastSync?: string
  }>({ isConfigured: false, accountCount: 0, locationCount: 0 })

  const [accounts, setAccounts] = useState<any[]>([])
  const [locations, setLocations] = useState<any[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSyncing, setIsSyncing] = useState(false)

  // Fetch real GMB statistics from Supabase
  const { data: gmbStats, isLoading: statsLoading } = useGMBStats()

  // Determine active tab based on current route
  const getActiveTab = () => {
    const path = location.pathname
    if (path.includes('/gmb/reviews')) return 'reviews'
    if (path.includes('/gmb/posts')) return 'posts'
    if (path.includes('/gmb/qa')) return 'qa'
    if (path.includes('/gmb/locations')) return 'locations'
    if (path.includes('/gmb/insights')) return 'insights'
    if (path.includes('/gmb/sync')) return 'sync'
    return 'overview'
  }

  const [activeTab, setActiveTab] = useState(getActiveTab())

  // Fetch locations from Supabase
  const { data: dbLocations = [], isLoading: dbLoading } = useQuery({
    queryKey: ['gmb-locations-db'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gmb_locations')
        .select('*')
        .order('location_name')

      if (error) throw error
      return data || []
    },
    enabled: oauthStatus.isConfigured
  })

  // Check OAuth status and load data on mount
  useEffect(() => {
    checkAuthAndLoadData()
  }, [])

  // Update locations when database data changes
  useEffect(() => {
    if (dbLocations.length > 0 && !isLoading) {
      const formattedLocations = dbLocations.map(loc => ({
        id: loc.gmb_location_id,
        account_id: loc.gmb_account_id || '',
        account_name: loc.franchise_region || 'FixMyBrick',
        name: loc.location_name,
        address: `${loc.address}, ${loc.city}, ${loc.province} ${loc.postal_code}`,
        phone: loc.phone_number,
        website: loc.website_url,
        place_id: loc.google_place_id,
        city: loc.city,
        province: loc.province,
        postal_code: loc.postal_code,
        isFromDatabase: true
      }))
      setLocations(formattedLocations)

      // Select first location if none selected
      if (!selectedLocation && formattedLocations.length > 0) {
        setSelectedLocation(formattedLocations[0].id)
      }
    }
  }, [dbLocations, isLoading])

  // Update active tab when route changes
  useEffect(() => {
    setActiveTab(getActiveTab())
  }, [location.pathname])

  // Handle tab change by navigation
  const handleTabChange = (value: string) => {
    if (value === 'overview') {
      navigate('/integrations/gmb')
    } else {
      navigate(`/integrations/gmb/${value}`)
    }
  }

  const checkAuthAndLoadData = async () => {
    setIsLoading(true)
    try {
      // Check OAuth status
      const status = await checkOAuthStatus()
      console.log('GMBLayout - OAuth Status:', status)
      setOauthStatus(status)

      // Don't load from worker API anymore - we'll use Supabase data
      // The database query will automatically load via React Query
    } catch (error) {
      console.error('Error loading GMB data:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSyncAccounts = async () => {
    // Check if OAuth is already configured
    if (oauthStatus.isConfigured) {
      // If already configured, just sync accounts
      handleDiscoverAccounts()
    } else {
      // If not configured, start OAuth flow
      handleOAuthFlow()
    }
  }

  const handleOAuthFlow = async () => {
    setIsSyncing(true)
    try {
      // Generate OAuth URL
      const result = await generateOAuthUrl()
      console.log('OAuth URL result:', result)

      if (result.success && result.data?.auth_url) {
        // Open OAuth URL in new window
        const authWindow = window.open(result.data.auth_url, 'gmb-oauth', 'width=600,height=700')

        // Listen for OAuth completion
        const checkInterval = setInterval(() => {
          if (authWindow?.closed) {
            clearInterval(checkInterval)
            // Check if OAuth was successful by reloading accounts
            setTimeout(() => {
              checkAuthAndLoadData()
            }, 2000)
          }
        }, 1000)
      } else {
        console.error('Failed to generate OAuth URL:', result.error)
      }
    } catch (error) {
      console.error('Error starting OAuth flow:', error)
    } finally {
      setIsSyncing(false)
    }
  }

  const handleDiscoverAccounts = async () => {
    setIsSyncing(true)
    try {
      const result = await discoverAllAccounts()
      if (result.success && result.data) {
        setAccounts(result.data.accounts)
        setLocations(result.data.locations)
        setOauthStatus({
          isConfigured: true,
          accountCount: result.data.total_accounts,
          locationCount: result.data.total_locations,
          lastSync: result.data.discovered_at
        })

        // Select first location
        if (result.data.locations.length > 0) {
          setSelectedLocation(result.data.locations[0].id)
        }
      }
    } catch (error) {
      console.error('Error discovering accounts:', error)
    } finally {
      setIsSyncing(false)
    }
  }

  const handleExportData = async () => {
    try {
      // Export GMB data as CSV
      const exportData = {
        locations: locations.map(loc => ({
          name: loc.name,
          address: loc.address,
          phone: loc.phone,
          website: loc.website,
          place_id: loc.place_id,
          city: loc.city,
          province: loc.province
        })),
        stats: {
          total_locations: locations.length,
          total_accounts: accounts.length,
          oauth_configured: oauthStatus.isConfigured,
          last_sync: oauthStatus.lastSync
        }
      }

      // Convert to CSV
      const csv = [
        'GMB Export Data',
        `Export Date: ${new Date().toISOString()}`,
        '',
        'Locations:',
        'Name,Address,Phone,Website,Place ID,City,Province',
        ...exportData.locations.map(loc =>
          `"${loc.name}","${loc.address}","${loc.phone || ''}","${loc.website || ''}","${loc.place_id || ''}","${loc.city}","${loc.province}"`
        ),
        '',
        'Statistics:',
        `Total Locations: ${exportData.stats.total_locations}`,
        `Total Accounts: ${exportData.stats.total_accounts}`,
        `OAuth Configured: ${exportData.stats.oauth_configured}`,
        `Last Sync: ${exportData.stats.last_sync || 'Never'}`
      ].join('\n')

      // Download file
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `gmb-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting data:', error)
    }
  }

  // Not connected state
  if (!oauthStatus.isConfigured && !isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Google My Business</h1>
          <p className="text-muted-foreground">
            Manage your business listings, reviews, and customer engagement
          </p>
        </div>

        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="rounded-full bg-primary/10 p-3 mb-4">
              <Building className="h-8 w-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold mb-2">Connect Google My Business</h3>
            <p className="text-muted-foreground text-center max-w-md mb-6">
              Connect your Google Business Profile to manage reviews, posts, and analytics across all your locations
            </p>
            <div className="flex gap-3">
              <Button size="lg" onClick={() => window.open('/integrations/gmb/setup', '_blank')}>
                <Globe className="mr-2 h-4 w-4" />
                Connect GMB Account
              </Button>
              <Button variant="outline" size="lg" onClick={() => window.open('https://docs.fixmybrick.com/gmb-setup', '_blank')}>
                Learn More
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground">Loading GMB data...</p>
          </div>
        </div>
      </div>
    )
  }

  const selectedLocationData = locations.find(loc => loc.id === selectedLocation)

  return (
    <GMBAuthGuard>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Google My Business</h1>
            <p className="text-muted-foreground">
              {locations.length} locations across {accounts.length} accounts
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncAccounts}
              disabled={isSyncing}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
              Sync Accounts
            </Button>
            <Button size="sm" onClick={handleExportData}>
              <Download className="mr-2 h-4 w-4" />
              Export Data
            </Button>
            <Button variant="ghost" size="sm">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Locations</CardTitle>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{locations.length}</div>
              <p className="text-xs text-muted-foreground">
                Across {accounts.length} accounts
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Avg Rating</CardTitle>
              <Star className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">{gmbStats?.averageRating || 0}</div>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map(i => (
                        <Star
                          key={i}
                          className={`h-3 w-3 ${i <= Math.round(gmbStats?.averageRating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    {(gmbStats?.ratingChange ?? 0) >= 0 ? (
                      <TrendingUp className="h-3 w-3 text-green-500" />
                    ) : (
                      <TrendingDown className="h-3 w-3 text-red-500" />
                    )}
                    {(gmbStats?.ratingChange ?? 0) >= 0 ? '+' : ''}{gmbStats?.ratingChange || 0} from last month
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Reviews</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{gmbStats?.totalReviews?.toLocaleString() || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    +{gmbStats?.reviewsThisMonth || 0} this month
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Response Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <Skeleton className="h-8 w-20" />
              ) : (
                <>
                  <div className="text-2xl font-bold">{gmbStats?.responseRate || 0}%</div>
                  <Progress value={gmbStats?.responseRate || 0} className="mt-2" />
                  <p className="text-xs text-muted-foreground mt-1">
                    {gmbStats?.pendingResponses || 0} pending responses
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
          <TabsList className="flex w-full overflow-x-auto md:grid md:grid-cols-7 gap-1 pb-1 md:pb-0">
            <TabsTrigger value="overview" className="shrink-0">Overview</TabsTrigger>
            <TabsTrigger value="reviews" className="shrink-0">
              Reviews
              {(gmbStats?.pendingResponses ?? 0) > 0 && (
                <Badge variant="destructive" className="ml-2 text-xs">
                  {gmbStats?.pendingResponses}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="posts" className="shrink-0">Posts</TabsTrigger>
            <TabsTrigger value="insights" className="shrink-0">Insights</TabsTrigger>
            <TabsTrigger value="qa" className="shrink-0">Q&A</TabsTrigger>
            <TabsTrigger value="locations" className="shrink-0">Locations</TabsTrigger>
            <TabsTrigger value="sync" className="shrink-0">
              <RefreshCw className="h-3 w-3 mr-1" />
              Sync
            </TabsTrigger>
          </TabsList>

          {/* Router outlet for child routes */}
          <Outlet context={{ locations, selectedLocation, setSelectedLocation, selectedLocationData }} />
        </Tabs>
      </div>
    </GMBAuthGuard>
  )
}